import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';

const env = typeof process !== 'undefined' && process.env ? process.env : {};

export const TENANT_ID = env.REACT_APP_ENTRA_TENANT_ID || 'ca1bdd86-4867-423e-9e7c-1c9fecfb2006';
export const CLIENT_ID = env.REACT_APP_ENTRA_CLIENT_ID || '168bb21b-aa3b-461e-a30c-fd35e51249e0';
const AUTHORITY_HOST = env.REACT_APP_ENTRA_AUTHORITY_HOST || `${TENANT_ID}.ciamlogin.com`;
const API_SCOPE = String(env.REACT_APP_ENTRA_API_SCOPE || '').trim();

const loginScopes = ['openid', 'profile', 'offline_access'];
const requestedScopes = API_SCOPE ? [...loginScopes, API_SCOPE] : loginScopes;

const isAuthDebugEnabled = () => (
  env.NODE_ENV !== 'production'
  || ['localhost', '127.0.0.1'].includes(window.location.hostname)
);

const decodeJwtPayload = (token = '') => {
  try {
    const [, payload] = String(token).split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

const summarizeToken = (token = '') => {
  const claims = decodeJwtPayload(token);
  if (!claims) {
    return {
      present: Boolean(token),
      segmentCount: String(token || '').split('.').length,
      readableClaims: false,
    };
  }

  const expiresAt = claims.exp ? new Date(claims.exp * 1000).toISOString() : '';
  const issuedAt = claims.iat ? new Date(claims.iat * 1000).toISOString() : '';
  return {
    present: Boolean(token),
    segmentCount: String(token || '').split('.').length,
    readableClaims: true,
    tokenUse: claims.idtyp || claims.typ || '',
    audience: claims.aud || '',
    issuer: claims.iss || '',
    tenant: claims.tid || '',
    subjectPresent: Boolean(claims.sub),
    emailClaimPresent: Boolean(claims.email || claims.preferred_username || claims.upn || claims.emails),
    scope: claims.scp || '',
    issuedAt,
    expiresAt,
  };
};

const logAuthDebug = (label, details) => {
  if (!isAuthDebugEnabled()) return;
  console.groupCollapsed(`[Auth] ${label}`);
  console.info(details);
  console.groupEnd();
};

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://${AUTHORITY_HOST}/${TENANT_ID}`,
    knownAuthorities: [AUTHORITY_HOST],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
});

let initializePromise;

export const initializeAuthentication = async () => {
  if (!initializePromise) {
    // Promise.resolve also supports MSAL-compatible test/runtime adapters whose
    // initialize method completes synchronously.
    initializePromise = Promise.resolve(msalInstance.initialize()).then(async () => {
      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
      }
      const accounts = msalInstance.getAllAccounts() || [];
      const account = msalInstance.getActiveAccount() || accounts[0];
      if (account) msalInstance.setActiveAccount(account);
      return redirectResult;
    });
  }
  return initializePromise;
};

export const getAuthenticatedAccount = () => (
  msalInstance.getActiveAccount() || (msalInstance.getAllAccounts() || [])[0] || null
);

export const signIn = async () => {
  await initializeAuthentication();
  try {
    const result = await msalInstance.loginPopup({ scopes: requestedScopes });
    if (result?.account) msalInstance.setActiveAccount(result.account);
    return result;
  } catch (error) {
    if (error instanceof BrowserAuthError && [
      'popup_window_error',
      'empty_window_error',
      'monitor_popup_timeout',
    ].includes(error.errorCode)) {
      await msalInstance.loginRedirect({ scopes: requestedScopes });
      return null;
    }
    throw error;
  }
};

export const signOut = async () => {
  await initializeAuthentication();
  const account = getAuthenticatedAccount();
  return msalInstance.logoutPopup({
    account,
    postLogoutRedirectUri: window.location.origin,
  });
};

export const acquireApiToken = async () => {
  await initializeAuthentication();
  const account = getAuthenticatedAccount();
  if (!account) {
    logAuthDebug('No active account for API token', {
      tenantId: TENANT_ID,
      clientId: CLIENT_ID,
      authorityHost: AUTHORITY_HOST,
      apiScopeConfigured: Boolean(API_SCOPE),
    });
    return '';
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: requestedScopes,
    });
    // An exposed API scope returns an access token. Until that scope is
    // configured, the signed ID token still provides a verifiable identity.
    const selectedToken = (API_SCOPE ? result.accessToken : result.idToken) || result.accessToken || '';
    logAuthDebug('Silent API token acquired', {
      tenantId: TENANT_ID,
      clientId: CLIENT_ID,
      authorityHost: AUTHORITY_HOST,
      apiScopeConfigured: Boolean(API_SCOPE),
      requestedScopes,
      selectedTokenType: API_SCOPE ? 'accessToken' : 'idTokenFallback',
      accessToken: summarizeToken(result.accessToken),
      idToken: summarizeToken(result.idToken),
      selectedToken: summarizeToken(selectedToken),
    });
    return selectedToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) throw error;
    const result = await msalInstance.acquireTokenPopup({
      account,
      scopes: requestedScopes,
    });
    const selectedToken = (API_SCOPE ? result.accessToken : result.idToken) || result.accessToken || '';
    logAuthDebug('Popup API token acquired', {
      tenantId: TENANT_ID,
      clientId: CLIENT_ID,
      authorityHost: AUTHORITY_HOST,
      apiScopeConfigured: Boolean(API_SCOPE),
      requestedScopes,
      selectedTokenType: API_SCOPE ? 'accessToken' : 'idTokenFallback',
      accessToken: summarizeToken(result.accessToken),
      idToken: summarizeToken(result.idToken),
      selectedToken: summarizeToken(selectedToken),
    });
    return selectedToken;
  }
};

export const API_SCOPE_CONFIGURED = Boolean(API_SCOPE);
